import React from 'react';

import { EmailFormValues } from 'types/emailFormTypes';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { useForm } from 'hooks/useForm';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { useAsyncActionViewScreen } from 'hooks/useAsyncViewScreen';
import { validateEmailFormValues__depricated } from 'utils/emailFormUtils';
import { useServiceState } from 'hooks/useServiceState';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { CustomFieldsetComponent } from 'design/customFieldset/customFieldsetComponents';
import { CustomInputComponent } from 'design/customInput/customInputComponents';

type TProps = {
  clientId: number;
  values: EmailFormValues;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ClientDetailsLoginEditor = (props: TProps) => {
  const {
    clientId,
    values: initialValues,
    onCancel,
    onSubmitDidSucceed,
  } = props;

  const intl = useIntlDictionary();

  const { updateClientLogin: updateClientLoginService } = useServices();

  // Валидаторы формы.
  const validator = React.useMemo(
    () => validateEmailFormValues__depricated(intl),
    [intl]
  );

  // Состояние формы, функция для валидирования,
  // хэндлер изменений инпута.
  const { values, validation, validate, onChange } = useForm({
    initialValues,
    validator,
  });

  // Состояние запроса на обновление логина,
  // функция отправки запроса.
  const [updateLoginState, updateLogin] = useServiceState(
    updateClientLoginService
  );

  // Эффект следит за состоянием запроса на обновление
  // логина и вызывает соответствующий колбэк с задержкой,
  // чтобы успел посветиться индикатор успеха.
  React.useEffect(() => {
    if (updateLoginState && updateLoginState.resolved) {
      return setCustomTimeout(() => {
        onSubmitDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [onSubmitDidSucceed, updateLoginState, values]);

  // Хэндлер сабмита формы, валидирует форму и в
  // случае успеха отправляет запрос на обновление логина.
  const onSubmit = React.useCallback(() => {
    validate(() => {
      updateLogin({ clientId, nextLogin: values.email });
    });
  }, [validate, updateLogin, clientId, values]);

  // Актуальный экран.
  const screen = useAsyncActionViewScreen(updateLoginState);

  const hasViolations = !!validation.email;

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {intl.clientDetails.loginEditor.title}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={hasViolations}
        onSubmit={onSubmit}
      >
        <SidebarModalSectionComponent
          content={
            <CustomFieldsetComponent disabled={screen !== 'ACTIVE'}>
              <CustomInputComponent
                value={values.email}
                errorMessage={validation.email}
                name="email"
                placeholder={intl.nextEmailInputPlaceholder}
                onChange={onChange}
              />
            </CustomFieldsetComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
          navigation={null}
        />
      </FormControlComponent>
    </>
  );
};
